const FRACTIONAL_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const FRACTIONAL_BASE = FRACTIONAL_ALPHABET.length;
const FRACTIONAL_MIN = 0;
const FRACTIONAL_MAX = FRACTIONAL_BASE - 1;

function assertValidKey(key: string): void {
  for (const char of key) {
    if (FRACTIONAL_ALPHABET.indexOf(char) === -1) {
      throw new Error(`Invalid fractional-order key character: "${char}"`);
    }
  }
}

function normalizeFractionalKey(key: string | null | undefined): string | null {
  if (typeof key !== 'string' || key.length === 0) {
    return null;
  }

  try {
    assertValidKey(key);
    return key;
  } catch {
    return null;
  }
}

function indexToChar(index: number): string {
  return FRACTIONAL_ALPHABET[index]!;
}

function charToIndex(char: string): number {
  const index = FRACTIONAL_ALPHABET.indexOf(char);
  if (index === -1) {
    throw new Error(`Invalid fractional-order key character: "${char}"`);
  }
  return index;
}

/**
 * Fractional key 用の順序比較（locale 非依存のバイト順）。
 */
export function compareFractionalKeys(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  return a < b ? -1 : 1;
}

/**
 * a < result < b を満たす文字列キーを生成する。
 * a/b は null を許容し、null は無限端点として扱う。
 */
export function generateFractionalKeyBetween(a: string | null, b: string | null): string {
  if (a !== null) {
    assertValidKey(a);
  }
  if (b !== null) {
    assertValidKey(b);
  }

  if (a !== null && b !== null && a >= b) {
    throw new Error('Invalid bounds: lower key must be less than upper key');
  }

  const lower = a ?? '';
  const upper = b ?? '';
  let prefix = '';
  let cursor = 0;

  while (true) {
    const lowerIndex = cursor < lower.length
      ? charToIndex(lower[cursor]!)
      : FRACTIONAL_MIN;
    const upperIndex = cursor < upper.length
      ? charToIndex(upper[cursor]!)
      : FRACTIONAL_MAX;

    if (lowerIndex === upperIndex) {
      prefix += indexToChar(lowerIndex);
      cursor += 1;
      continue;
    }

    if (upperIndex - lowerIndex > 1) {
      const midpoint = Math.floor((lowerIndex + upperIndex) / 2);
      return `${prefix}${indexToChar(midpoint)}`;
    }

    prefix += indexToChar(lowerIndex);
    cursor += 1;
  }
}

export function generateFractionalKeyAfter(key: string | null): string {
  return generateFractionalKeyBetween(key, null);
}

export function generateFractionalKeyBefore(key: string | null): string {
  return generateFractionalKeyBetween(null, key);
}

/**
 * 兄弟要素の順序キー配列と挿入位置から、壊れた境界値を許容しつつ新しい順序キーを生成する。
 */
export function generateFractionalKeyForInsertion(
  orderedKeys: Array<string | null | undefined>,
  insertIndex: number
): string {
  const normalizedKeys = orderedKeys.map((key) => normalizeFractionalKey(key));
  const boundedIndex = Math.max(0, Math.min(normalizedKeys.length, Math.trunc(insertIndex)));

  const tryGenerate = (lower: string | null, upper: string | null): string | null => {
    try {
      return generateFractionalKeyBetween(lower, upper);
    } catch {
      return null;
    }
  };

  const directLower = boundedIndex > 0 ? normalizedKeys[boundedIndex - 1] : null;
  const directUpper = boundedIndex < normalizedKeys.length ? normalizedKeys[boundedIndex] : null;
  const directKey = tryGenerate(directLower, directUpper);
  if (directKey) {
    return directKey;
  }

  let leftNearest: string | null = null;
  for (let i = boundedIndex - 1; i >= 0; i -= 1) {
    const candidate = normalizedKeys[i];
    if (candidate) {
      leftNearest = candidate;
      break;
    }
  }

  let rightNearest: string | null = null;
  for (let i = boundedIndex; i < normalizedKeys.length; i += 1) {
    const candidate = normalizedKeys[i];
    if (candidate) {
      rightNearest = candidate;
      break;
    }
  }

  const nearestKey = tryGenerate(leftNearest, rightNearest);
  if (nearestKey) {
    return nearestKey;
  }

  const appendKey = leftNearest ? tryGenerate(leftNearest, null) : null;
  if (appendKey) {
    return appendKey;
  }

  const prependKey = rightNearest ? tryGenerate(null, rightNearest) : null;
  if (prependKey) {
    return prependKey;
  }

  return generateFractionalKeyBetween(null, null);
}
